import React, { useState, useContext } from 'react';
import { TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { AppContext } from '../../App';
import useGlynt from '../hooks/useGlynt';
import errorResponses from '../../errorResponses';
import { validateLabel } from '../../utils/validationUtils';

interface OrgNewDialogProps {
  open: boolean;
  onClose: () => void;
  orgLabels: string[];
}

const OrgNewDialog: React.FC<OrgNewDialogProps> = ({ open, onClose, orgLabels }) => {
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const [name, setName] = useState('');

  const validation = validateLabel(name, orgLabels);

  const createOrg = useMutation({
    mutationFn: async (label: string) => (await glynt).org.create(label),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['orgs'] });
      setToast({ variant: 'success', open: true, message: 'Organization created successfully.' });
      setName('');
      onClose();
    },
    onError: (error: Error) => {
      setToast({
        variant: 'error',
        open: true,
        message: error.message || errorResponses.failedToCreateOrg,
      });
    },
  });

  const handleClose = () => {
    if (!createOrg.isPending) {
      setName('');
      onClose();
    }
  };

  const handleSubmit = () => {
    createOrg.mutate(name);
  };

  return (
    <DialogGlynt open={open} closeModal={handleClose} cypressId="dialog-new-org" loading={createOrg.isPending}>
      <DialogTitleGlynt title="New Organization" closeModal={handleClose} loading={createOrg.isPending} />
      <DialogContentGlynt>
        <TextField
          autoComplete="off"
          data-cy="input-add-org"
          label="Organization Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.length > 0 && !validation.isValid}
          helperText={name.length > 0 && !validation.isValid ? validation.errorMessage : ''}
          autoFocus
        />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={handleClose}
        cancelCypressId="btn-cancel-new-org"
        submitCypressId="btn-create-org"
        clearCypressId=""
        submitButtonText="Create"
        submitDisabled={!validation.isValid}
        submitLoading={createOrg.isPending}
      />
    </DialogGlynt>
  );
};

export default OrgNewDialog;
